import DayOfWeek from "../../components/DayOfWeek";
import Time from "../../components/Time";
import { Calendar, MapPin, Clock } from "lucide-react";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * ClassSchedule Component
 * Displays class schedule in compact horizontal card.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.htClass - The class object containing class days
 *
 * @author Forge
 * @date 2026-04-10
 * @version 3.0.0
 */
export default function ClassSchedule({ htClass }) {
  const { primaryColor } = usePrimaryColor();
  return (
    <div className="flex-1 min-w-[280px] bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <h3 className="text-base font-black text-slate-900 flex items-center gap-2 uppercase tracking-tight">
          <Calendar size={16} className={`text-${primaryColor}-800`} /> Schedule
        </h3>
      </div>

      {/* Schedule List */}
      <div className="p-3 overflow-y-auto max-h-[280px]">
        <div className="flex flex-col gap-2">
          {htClass.classDays.map((classDay, index) => (
            <div
              key={classDay._id || index}
              className={`flex flex-col gap-1.5 p-2.5 bg-slate-50/50 rounded-xl border border-slate-100 hover:border-${primaryColor}-50 hover:bg-${primaryColor}-50 transition-all`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 font-bold text-slate-800 text-xs uppercase tracking-wide">
                  <DayOfWeek day={classDay.day} />
                </div>
                <div className="bg-white px-2 py-0.5 rounded-lg border border-slate-200 text-[0.6rem] font-bold text-slate-400 flex items-center gap-1">
                  <MapPin size={10} />
                  {classDay.room || "No room"}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className={`flex-1 h-[1px] bg-${primaryColor}-50 rounded-full`} />
                <div className={`bg-${primaryColor}-800 text-white px-2.5 py-1 rounded-lg text-[0.65rem] font-bold flex items-center gap-1.5`}>
                  <Clock size={10} strokeWidth={3} />
                  <Time time={classDay.startTime} /> - <Time time={classDay.endTime} />
                </div>
              </div>
            </div>
          ))}
          {htClass.classDays.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm italic">
              No days scheduled
            </div>
          )}
        </div>
      </div>
    </div>
  );
}